import { useCallback, useEffect, useRef, useState } from "react";
import { Stack, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import { FormProvider } from "@app/components";
import { useRequestState } from "@app/hooks";
import { Strings } from "@app/constants";

import { editProfileSchema } from "../validation";
import { selectMyProfileState, selectUserProfileState } from "../selector";
import { updateUserProfile } from "../slice";
import { ProfileAvatar } from "./profiles-avatar";
import { ProfileForm } from "./profiles-forms";
import { SaveButton } from "./save-buttons";
import { EditProfileFormValues } from "../types";

const EditProfile = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<EditProfileFormValues>({
    resolver: yupResolver(editProfileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      state: "",
      city: "",
      zipCode: "",
    },
  });

  const fileInputRef: any = useRef(null);

  const [avatarPreview, setAvatarPreview] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files && files[0];

    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };

      reader.readAsDataURL(file);
      setSelectedFile(file);
    }
  };

  const handleEditIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const { data } = useRequestState({
    stateSelector: selectMyProfileState,
  });

  useEffect(() => {
    if (!data) return;

    methods.reset({
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      phone: data?.phone || "",
      email: data?.email || "",
      profileImage: data?.profileImage || "",
      state: data?.state || "",
      city: data?.city || "",
      zipCode: data?.zipCode || "",
    });

    if (data?.profileImage) {
      setAvatarPreview(data?.profileImage);
    }
  }, [data, methods]);


  const onSubmit = (form: EditProfileFormValues) => {
    dispatch(
      updateUserProfile({
        id: data?.id,
        firstName: form?.firstName,
        lastName: form?.lastName,
        phone: form?.phone,
        email: form?.email,
        state: form?.state,
        city: form?.city,
        zipCode: form?.zipCode,
        profileImage: selectedFile,
      }),
    );
  };

  const handleNavigation = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const { loading } = useRequestState({
    stateSelector: selectUserProfileState,
    errorShown: true,
    successMessageShown: true,
    onSuccess: handleNavigation,
  });

  return (
    <Stack
      sx={{
        backgroundColor: theme.palette.common.white,
        alignItems: "center",
        py: 4,
        px: 2,
        gap: 2,
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        gutterBottom
        sx={{
          fontFamily: "Work Sans",
          fontWeight: "700",
        }}
      >
        {Strings.myProfile.editProfile}
      </Typography>

      <ProfileAvatar
        avatarPreview={avatarPreview}
        firstName={data?.firstName}
        lastName={data?.lastName}
        onFileChange={handleFileChange}
        onEditClick={handleEditIconClick}
        fileInputRef={fileInputRef}
      />

      <FormProvider methods={methods}>
        <Stack alignItems="center" width="100%" spacing={4}>
          <ProfileForm />
          <SaveButton
            onClick={methods.handleSubmit(onSubmit)}
            loading={loading}
          />
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export { EditProfile };
